import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBoardCellSizeFromArea, getBoardFontSizeFromArea } from '../../utils/gameLayout';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface SuguruPuzzle {
  id: number;
  size: number;
  puzzle: number[];
  solution: number[];
  regions: number[][];
  regionMap: number[];
  nivelNeureka: number;
  nivelNeurekaNombre: string;
  nivelNeurekaEmoji: string;
}

export default function SuguruGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();

  const [puzzle, setPuzzle] = useState<SuguruPuzzle | null>(null);
  const [boardState, setBoardState] = useState<number[]>([]);
  const [history, setHistory] = useState<number[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startTime] = useState(Date.now());
  const [moves, setMoves] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hintInfo, setHintInfo] = useState<any>(null);
  const [highlightedCell, setHighlightedCell] = useState<number | null>(null);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [selectedCell, setSelectedCell] = useState<number | null>(null);

  const dificultadOrden = parseInt(dificultad || '1');
  const isFirstLoad = useRef(true);
  const [forceReload, setForceReload] = useState(0);

  // Hook para gestionar el progreso
  const { saveProgress: saveGameProgress } = useGameProgress('suguru', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Cargar puzzle
  useEffect(() => {
    const loadPuzzle = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await api.get('/suguru/board', {
          params: { dificultad: dificultadOrden }
        });

        const puzzleData: SuguruPuzzle = response.data.data;
        setPuzzle(puzzleData);

        // Inicializar tablero con las pistas
        setBoardState([...puzzleData.puzzle]);
        setHistory([[...puzzleData.puzzle]]);
        setHistoryIndex(0);
        setMoves(0);
        setIsCompleted(false);
        setShowVictory(false);
      } catch (err: any) {
        console.error('Error al cargar puzzle:', err);
        setError(err.response?.data?.error || 'Error al cargar el puzzle');
      } finally {
        setLoading(false);
        isFirstLoad.current = false;
      }
    };

    loadPuzzle();
  }, [dificultadOrden, forceReload]);

  // Guardar estado en historial
  const saveState = (newState: number[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newState]);

    // Limitar historial a 50 estados
    if (newHistory.length > 50) {
      newHistory.shift();
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  };

  // Click en celda: seleccionar
  const handleCellClick = (index: number) => {
    if (isCompleted || !puzzle) return;
    if (puzzle.puzzle[index] !== 0) return; // celda fija
    setSelectedCell(prev => prev === index ? null : index);
  };

  // Colocar valor en celda seleccionada
  const setCellValue = (index: number, value: number) => {
    if (isCompleted || !puzzle) return;
    if (puzzle.puzzle[index] !== 0) return;

    const newState = [...boardState];
    newState[index] = value;
    setBoardState(newState);
    setMoves(prev => prev + 1);
    saveState(newState);
    checkVictory(newState, false);
  };

  // Teclado
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (selectedCell === null || !puzzle) return;
      const regionId = puzzle.regionMap[selectedCell];
      const regionSize = puzzle.regions[regionId].length;

      if (e.key >= '1' && e.key <= '9') {
        const num = parseInt(e.key);
        if (num <= regionSize) setCellValue(selectedCell, num);
      } else if (e.key === '0' || e.key === 'Backspace' || e.key === 'Delete') {
        setCellValue(selectedCell, 0);
      } else if (e.key === 'Escape') {
        setSelectedCell(null);
      } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const size = puzzle.size;
        const r = Math.floor(selectedCell / size);
        const c = selectedCell % size;
        let nr = r, nc = c;
        if (e.key === 'ArrowUp') nr = Math.max(0, r - 1);
        else if (e.key === 'ArrowDown') nr = Math.min(size - 1, r + 1);
        else if (e.key === 'ArrowLeft') nc = Math.max(0, c - 1);
        else if (e.key === 'ArrowRight') nc = Math.min(size - 1, c + 1);
        const next = nr * size + nc;
        if (puzzle.puzzle[next] === 0) setSelectedCell(next);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedCell, puzzle, boardState]);

  // Deshacer
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setBoardState([...history[historyIndex - 1]]);
    }
  };

  // Rehacer
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setBoardState([...history[historyIndex + 1]]);
    }
  };

  // Reiniciar
  const handleReset = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres reiniciar el tablero?')) return;

    const resetBoard = [...puzzle.puzzle];
    setBoardState(resetBoard);
    setHistory([resetBoard]);
    setHistoryIndex(0);
    setMoves(0);
    setIsCompleted(false);
    setShowVictory(false);
    setError('');
  };

  // Verificar victoria automáticamente
  const checkVictory = async (state: number[], manual: boolean) => {
    if (!puzzle || isCompleted) return;

    // Verificar que todas las celdas estén llenas
    const isFull = !state.includes(0);

    if (!isFull) {
      if (manual) setError('Aún hay celdas vacías');
      return;
    }

    // Si está completo, validar con el backend
    try {
      const response = await api.post('/suguru/validate', {
        puzzleId: puzzle.id,
        solution: state,
        regionMap: puzzle.regionMap,
        size: puzzle.size
      });

      if (response.data.data.correct) {
        setIsCompleted(true);
        const tiempo = Math.floor((Date.now() - startTime) / 1000);
        saveProgress(tiempo, moves);
        setShowVictory(true);
        setError('');
      } else if (manual) {
        setError(response.data.data.message || 'La solución no es correcta');
      }
    } catch (err: any) {
      console.error('Error al validar:', err);
      if (manual) setError('Error al validar la solución');
    }
  };

  // Verificar solución manualmente
  const handleCheckSolution = async () => {
    checkVictory(boardState, true);
  };

  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres ver la solución? Esto no guardará tu progreso.')) return;
    setBoardState([...puzzle.solution]);
    saveState([...puzzle.solution]);
  };

  // Dar pista
  const handleHint = async () => {
    if (!puzzle) return;

    try {
      const response = await api.post('/suguru/hint', {
        currentState: boardState,
        regionMap: puzzle.regionMap,
        size: puzzle.size
      });

      const hint = response.data.data;
      setHintInfo(hint);
      setError('');

      // Destacar celda sugerida
      if (hint.suggestion && hint.suggestion.row !== undefined) {
        const index = hint.suggestion.row * puzzle.size + hint.suggestion.col;
        setHighlightedCell(index);

        // Quitar highlight después de 3 segundos
        setTimeout(() => setHighlightedCell(null), 3000);
      }
    } catch (err: any) {
      console.error('Error al obtener hint:', err);
      setError('Error al obtener pista');
    }
  };

  // Guardar progreso
  const saveProgress = async (tiempo: number, movimientos: number) => {
    if (!puzzle) return;

    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: puzzle.id,
        tiempo,
        movimientos,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos,
        completado: true,
        datos_juego: {
          tablero_id: puzzle.id,
          size: puzzle.size,
        },
      });
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(state: number[], puz: SuguruPuzzle): Set<number> {
    const errores = new Set<number>();
    const { size, regionMap } = puz;

    // 1. Duplicados dentro de la misma región
    const regionCells = new Map<number, number[]>();
    for (let i = 0; i < state.length; i++) {
      const rid = regionMap[i];
      if (!regionCells.has(rid)) regionCells.set(rid, []);
      regionCells.get(rid)!.push(i);
    }
    for (const cells of regionCells.values()) {
      const seen = new Map<number, number>();
      for (const idx of cells) {
        const v = state[idx];
        if (!v) continue;
        if (seen.has(v)) { errores.add(seen.get(v)!); errores.add(idx); }
        else seen.set(v, idx);
      }
    }

    // 2. Iguales en celdas adyacentes (8 direcciones)
    for (let i = 0; i < state.length; i++) {
      const v = state[i];
      if (!v) continue;
      const r = Math.floor(i / size);
      const c = i % size;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          const nr = r + dr, nc = c + dc;
          if (nr < 0 || nr >= size || nc < 0 || nc >= size) continue;
          const j = nr * size + nc;
          if (state[j] === v) { errores.add(i); errores.add(j); }
        }
      }
    }

    return errores;
  }

  // Obtener bordes de región para una celda
  const getRegionBorders = (index: number): string => {
    if (!puzzle) return '';

    const row = Math.floor(index / puzzle.size);
    const col = index % puzzle.size;
    const regionId = puzzle.regionMap[index];

    let borderClass = '';

    // Arriba
    if (row === 0 || puzzle.regionMap[index - puzzle.size] !== regionId) {
      borderClass += ' border-t-4';
    }
    // Abajo
    if (row === puzzle.size - 1 || puzzle.regionMap[index + puzzle.size] !== regionId) {
      borderClass += ' border-b-4';
    }
    // Izquierda
    if (col === 0 || puzzle.regionMap[index - 1] !== regionId) {
      borderClass += ' border-l-4';
    }
    // Derecha
    if (col === puzzle.size - 1 || puzzle.regionMap[index + 1] !== regionId) {
      borderClass += ' border-r-4';
    }

    return borderClass;
  };

  // Tamaño de celda: ocupa el máximo espacio disponible en pantalla
  const cellSize = getBoardCellSizeFromArea(puzzle?.size ?? 6);
  const fontSize = getBoardFontSizeFromArea(puzzle?.size ?? 6, 0.45);

  const padEnabled = selectedCell !== null;
  // Sin celda seleccionada se enseña la región más grande del tablero, así la
  // barra ya nace con su ancho máximo y nunca pasa a dos filas al seleccionar.
  // Ojo: regions viene indexado desde 1, con un hueco en la posicion 0 que la
  // API serializa como null. Hay que filtrarlo antes de recorrerlo.
  const padSize = puzzle
    ? (selectedCell !== null
        ? puzzle.regions[puzzle.regionMap[selectedCell]]?.length ?? 0
        : Math.max(0, ...puzzle.regions.filter(Boolean).map(r => r.length)))
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-purple-700 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-purple-700 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar puzzle</div>
      </div>
    );
  }

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  const errores = modoAsistido ? calcularErrores(boardState, puzzle) : new Set<number>();

  return (
    <div className="h-full bg-gradient-to-br from-purple-900 to-purple-700 flex flex-col">
      {/* Banner de Sesión */}
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🔢 Suguru</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dificultadOrden === d.orden) setForceReload(prev => prev + 1);
                else navigate(`/juegos/suguru/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden ? 'bg-white text-purple-900' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          <button onClick={handleUndo} disabled={historyIndex <= 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={handleCheckSolution} disabled={isCompleted} title="Verificar solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
          </button>
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={() => setForceReload(prev => prev + 1)} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01"/></svg>
          </button>
        </div>
      </div>

      {/* Error/hint messages */}
      {error && (
        <div className="mx-3 mt-2 p-2 bg-purple-100 border border-purple-300 text-purple-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}
      {hintInfo && (
        <div className="mx-3 mt-2 p-3 bg-yellow-50 border-2 border-yellow-400 rounded-xl">
          <div className="flex items-start gap-2">
            <span className="text-2xl">{hintInfo.type !== 'none' ? '💡' : '🤔'}</span>
            <div className="flex-1 text-sm">
              <p className="font-bold text-gray-800">{hintInfo.title}</p>
              <p className="text-gray-700">{hintInfo.explanation}</p>
              {hintInfo.suggestion && (
                <p className="font-semibold text-yellow-800 mt-1">Sugerencia: {hintInfo.suggestion.action}</p>
              )}
            </div>
            <button onClick={() => setHintInfo(null)} className="text-gray-500 hover:text-gray-700 font-bold">✕</button>
          </div>
        </div>
      )}

      {/* Board area */}
      <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center p-2" style={{ containerType: 'size' }}>
        <div
          className="inline-grid bg-gray-900"
          style={{
            gridTemplateColumns: `repeat(${puzzle.size}, ${cellSize})`,
            gap: '0px',
          }}
        >
          {Array.from({ length: puzzle.size * puzzle.size }, (_, index) => {
            const value = boardState[index];
            const isOriginal = puzzle.puzzle[index] !== 0;
            const isHighlighted = highlightedCell === index;
            const isSelected = selectedCell === index;
            const hasError = errores.has(index);
            const borders = getRegionBorders(index);

            let bgColor = '#ffffff';
            let textColor = '#4b5563';
            let cursor = 'pointer';
            if (isHighlighted) {
              bgColor = '#fde047';
            } else if (hasError) {
              bgColor = isSelected ? '#fca5a5' : '#fecaca'; textColor = '#991b1b';
            } else if (isSelected) {
              bgColor = '#bfdbfe'; textColor = '#1e3a8a';
            } else if (isOriginal) {
              bgColor = '#fde68a'; textColor = '#92400e'; cursor = 'default';
            } else if (value !== 0) {
              bgColor = '#bbf7d0'; textColor = '#166534';
            }

            return (
              <div
                key={index}
                onClick={() => handleCellClick(index)}
                className={`flex items-center justify-center font-bold transition-all border-gray-900 ${borders} ${isHighlighted ? 'animate-pulse' : ''}`}
                style={{ width: cellSize, height: cellSize, fontSize, backgroundColor: bgColor, color: textColor, cursor, fontWeight: isOriginal ? 'bold' : undefined }}
              >
                {value !== 0 && value}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pad numérico virtual. Siempre presente: si apareciera solo al
          seleccionar, el tablero daria un salto al encogerse su hueco.
          Sin seleccion muestra la region mas grande, para que la barra no
          cambie de alto al elegir celda. */}
      {puzzle && (
        <div className={`bg-black/10 px-3 pt-2 pb-safe shrink-0 flex items-center justify-center gap-2 flex-wrap transition-opacity ${padEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
          {Array.from({ length: padSize }, (_, i) => i + 1).map(num => (
            <button key={num} onClick={() => { if (padEnabled) setCellValue(selectedCell!, num); }} disabled={!padEnabled}
              className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 text-white font-bold rounded-lg shadow active:scale-95 transition-all text-lg">
              {num}
            </button>
          ))}
          <button onClick={() => { if (padEnabled) setCellValue(selectedCell!, 0); }} disabled={!padEnabled} title="Borrar"
            className="w-11 h-11 ml-2 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg shadow active:scale-95 transition-all text-base">
            ✕
          </button>
        </div>
      )}

      {/* Modal Cómo se juega */}
      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Suguru"
        gameIcon="🔢"
        objective="Rellena cada región con números del 1 al N, donde N es el tamaño de la región."
        rules={[
          'Cada región (área delimitada por bordes gruesos) debe contener números del 1 hasta N, donde N es el número de celdas de esa región',
          'No puede haber números repetidos dentro de una misma región',
          'No puede haber números iguales en celdas adyacentes, incluidas las diagonales',
          'Una región de 3 celdas debe tener 1, 2, 3. Una región de 5 celdas debe tener 1, 2, 3, 4, 5',
        ]}
        howToPlay="Toca una celda vacía para seleccionarla (se resalta en azul). Luego pulsa el número en el teclado o en el pad numérico que aparece abajo. Usa ✕ para borrar. Las flechas del teclado permiten moverse entre celdas."
        author="Otto Janko"
      />

      {/* Modal de Victoria */}
      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: moves,
        }}
      />
    </div>
  );
}
